import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetServiceCategoryQuery,
  useUpdateServiceCategoryMutation,
} from "../redux/services/api";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import Icon from "../components/Icon";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";

const EditServicesCategory = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // RTK Query hooks
  const { data: categoryData, isLoading: loading } =
    useGetServiceCategoryQuery(id);
  const [updateCategory, { isLoading: saving }] =
    useUpdateServiceCategoryMutation();

  const [saveStatus, setSaveStatus] = useState("");
  const { t } = useTranslation();
  const messageType = useMessageType(saveStatus);

  const [formData, setFormData] = useState({
    title: "",
    titleAr: "",
    is_active: true,
  });

  const [originalCategory, setOriginalCategory] = useState(null);

  // Load category data when fetched
  useEffect(() => {
    if (categoryData && categoryData.category) {
      const category = categoryData.category;
      setOriginalCategory(category);
      setFormData({
        title: category.title || "",
        titleAr: category.title_ar || "",
        is_active: category.is_active !== false,
      });
    }
  }, [categoryData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      setSaveStatus(t("addEditCategory.pleaseLoginToSave"));
      return;
    }

    // Validate form
    if (!formData.title.trim()) {
      setSaveStatus(t("addEditCategory.pleaseEnterTitle"));
      return;
    }

    setSaveStatus("");

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("titleAr", formData.titleAr);
      submitData.append("is_active", formData.is_active ? "1" : "0");

      const result = await updateCategory({ id, body: submitData }).unwrap();

      if (result.success) {
        setSaveStatus(t("addEditCategory.categoryUpdatedSuccessfully"));
        setTimeout(() => {
          navigate("/dashboard/services-categories");
        }, 1500);
      } else {
        setSaveStatus(t("addEditCategory.errorUpdatingCategory"));
      }
    } catch (error) {
      console.error("Error updating category:", error);
      setSaveStatus(
        error.data?.message || t("addEditCategory.errorUpdatingCategory")
      );
    }
  };

  const handleCancel = () => {
    navigate("/dashboard/services-categories");
  };

  if (loading) {
    return (
      <div className="add-services-category">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("servicesDashboard.loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="add-services-category">
        <div className="auth-required">
          <h2>{t("addEditCategory.authenticationRequired")}</h2>
          <p>{t("addEditCategory.pleaseLoginToEdit")}</p>
          <button className="login-btn" onClick={() => navigate("/login")}>
            {t("servicesDashboard.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  if (!originalCategory) {
    return (
      <div className="add-services-category">
        <div className="auth-required">
          <h2>{t("addEditCategory.categoryNotFound")}</h2>
          <p>{t("addEditCategory.categoryNotFoundMessage")}</p>
          <button
            className="login-btn"
            onClick={() => navigate("/dashboard/services-categories")}
          >
            {t("servicesDashboard.backToCategories")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="add-services-category">
      <div className="page-header">
        <div className="header-content">
          <button type="button" className="back-btn" onClick={handleCancel}>
            <Icon name="arrow-left" size="16px" />
            {t("servicesDashboard.backToCategories")}
          </button>
          <h1>{t("addEditCategory.editCategory")}</h1>
          <p>{t("addEditCategory.editExistingCategory")}</p>
        </div>
      </div>

      <div className="form-container">
        <div className="form-card">
          <div className="form-content">
            <div className="form-group">
              <label>{t("addEditCategory.categoryTitleEnglish")}</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                placeholder={t("addEditCategory.enterCategoryTitle")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditCategory.categoryTitleArabic")}</label>
              <input
                type="text"
                value={formData.titleAr}
                onChange={(e) => handleInputChange("titleAr", e.target.value)}
                placeholder={t("addEditCategory.enterCategoryTitleAr")}
                className="form-input"
                dir="rtl"
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleInputChange("is_active", e.target.checked)
                  }
                />
                <span>{t("addEditCategory.activeCategory")}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="save-section">
        <StatusMessage
          message={saveStatus}
          type={messageType}
          show={!!saveStatus}
          autoClose={messageType === "success"}
          autoCloseDelay={3000}
          onClose={() => setSaveStatus("")}
        />
        <div className="save-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={handleCancel}
            disabled={saving}
          >
            {t("addEditCategory.cancel")}
          </button>
          <button
            type="button"
            className="save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="loading-spinner small"></div>
                {t("addEditCategory.saving")}
              </>
            ) : (
              <>
                <Icon name="save" size="16px" />
                {t("addEditCategory.save")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditServicesCategory;
